import { FC, ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, PopoverDialog, usePopoverDialog, Text, TextLink, Button } from '@frontend/design-system';

interface FilterChipPopoverProps {
  children: ReactNode;
  value: string;
  onClear: () => void;
  onApply: () => void;
  isClearDisabled: boolean;
  isApplyDisabled: boolean;
  shouldCloseOnClear?: boolean;
  trackingId?: string;
  Header?: ReactNode;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  clearButtonTrackingId?: string;
  applyButtonTrackingId?: string;
}

export const FilterChipPopover: FC<FilterChipPopoverProps> = ({
  children,
  value,
  onClear,
  onApply,
  isClearDisabled,
  isApplyDisabled,
  shouldCloseOnClear,
  trackingId,
  onKeyDown,
  Header,
  clearButtonTrackingId,
  applyButtonTrackingId,
}) => {
  const { t } = useTranslation('team-chat');

  const { getTriggerProps, getDialogProps, close, open, isOpen } = usePopoverDialog({
    placement: 'bottom',
    middlewareOptions: { offset: 8 },
  });

  const onClickClear = () => {
    onClear();
    if (shouldCloseOnClear) {
      close();
    }
  };

  const onClickApply = () => {
    onApply();
    close();
  };

  return (
    <>
      <Chip.DropdownFilter
        trackingId={trackingId}
        onClick={isOpen ? close : open}
        className='dropdownFilterChipField'
        css={styles.dropdownChip(isOpen)}
        {...getTriggerProps()}
      >
        <Text as='span' className='label-prefix'>
          {value}
        </Text>
      </Chip.DropdownFilter>
      <PopoverDialog {...getDialogProps()} onKeyDown={onKeyDown} css={styles.dialog}>
        {Header}
        <TextLink
          disabled={isClearDisabled}
          css={styles.clearButton}
          onClick={onClickClear}
          trackingId={clearButtonTrackingId}
        >
          {t('Clear')}
        </TextLink>
        {children}
        <Button
          onClick={onClickApply}
          disabled={isApplyDisabled}
          css={styles.applyButton}
          trackingId={applyButtonTrackingId}
        >
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </>
  );
};

const styles = {
  dropdownChip: (isOpen: boolean) => ({
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '100%',
    width: 'fit-content',
    svg: {
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
      height: theme.spacing(1),
      width: theme.spacing(1),
    },
  }),
  dialog: css({
    width: '318px',
    maxHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 0, 0.5),
  }),

  clearButton: css({
    fontWeight: theme.font.weight.bold,
    cursor: 'pointer',
    alignSelf: 'flex-end',
    margin: theme.spacing(0.5, 2, 0, 0),
  }),
  applyButton: css({
    margin: theme.spacing(1, 2, 2),
    width: 'fit-content',
  }),
};
